// The sample project the guided demo runs on.
//
// SAFETY: this project exists only in memory. Its id carries the `demo_`
// prefix, which App.tsx uses to suppress every write path — the debounced
// localStorage save, the 30-second autosave, the manual Save button and AMI
// persistence. Nothing here may ever reach Supabase or the project list,
// because it is fabricated customer data on a tool whose output is a permit
// document.
//
// It ships `results_unlocked: true` so Results renders without the paywall.
// That is the whole billing story: ResultsView reads
// `results_unlocked === true || isSubscribed`, so the lock screen never mounts
// and the free-trial button it owns is never reachable — `trial_used` cannot
// be spent by watching the demo.

import type { AMIDataPoint, Equipment, Project } from '../types/project';
import { getAutoFillValues } from './concurrency';
import { DWELLING_CHECKLIST } from './dwellingDefaults';

export const DEMO_PROJECT_ID = 'demo_guided_tour';

/** True for any project id belonging to the demo. */
export function isDemoProjectId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith('demo_');
}

/**
 * Deterministic PRNG, so the demo's peak demand — and therefore its PASS — is
 * the same every run and can be asserted in tests. Math.random would make the
 * headline number wander between viewings of the same demo.
 */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface DemoAmiOptions {
  /** Last timestamp in the series. The year runs backwards from here. */
  endDate: Date;
  intervalMinutes?: number;
}

/**
 * A year of 15-minute interval data for a Vermont house that heats
 * electrically: a winter-evening peak, a mild summer, and enough day-to-day
 * variation to look like a real meter rather than a sine wave.
 *
 * Shaped to land the annual peak near 10.2 kW, which against a 200 A / 240 V
 * service is a comfortable PASS with a heat pump added — the demo should show
 * the answer a contractor hopes for, not a marginal one that needs caveats.
 *
 * Built from UTC accessors, because the app reads every stored timestamp as
 * fixed-UTC wall clock (the meter's own clock): the cold snap below lands at
 * 6 PM on Jan 15 in every viewer's timezone.
 */
export function buildDemoAmiSeries({
  endDate,
  intervalMinutes = 15,
}: DemoAmiOptions): AMIDataPoint[] {
  const perDay = (24 * 60) / intervalMinutes;
  const days = 365;
  const total = days * perDay;
  const rand = mulberry32(0x7c0ad);
  const stepMs = intervalMinutes * 60 * 1000;
  const startMs = endDate.getTime() - (total - 1) * stepMs;

  const points: AMIDataPoint[] = new Array(total);
  for (let i = 0; i < total; i++) {
    const ts = new Date(startMs + i * stepMs);
    const hour = ts.getUTCHours() + ts.getUTCMinutes() / 60;
    // 0 at midsummer, 1 at midwinter.
    const monthPhase = (ts.getUTCMonth() + ts.getUTCDate() / 31) / 12;
    const winter = (Math.cos(2 * Math.PI * monthPhase) + 1) / 2;

    // Always-on load: fridge, network gear, standby.
    let kw = 0.45 + 0.1 * rand();
    // Morning and evening occupancy humps.
    kw += 1.5 * Math.exp(-Math.pow((hour - 7) / 1.3, 2));
    kw += 2.2 * Math.exp(-Math.pow((hour - 18.5) / 2.1, 2));
    // Heating, which is what makes the winter evening the annual peak.
    kw += winter * (2.6 + 2.4 * Math.exp(-Math.pow((hour - 19) / 3.2, 2)));
    // Mild summer cooling, deliberately below the winter peak.
    kw += (1 - winter) * 1.1 * Math.exp(-Math.pow((hour - 16) / 2.6, 2));
    // Appliance spikes: a range or dryer cycling. Deliberately capped below
    // the engineered peak below — a stray spike that outgrew it would move the
    // annual maximum to a random date and could land it outside winter.
    if (rand() < 0.01) kw += 1.0 + 0.8 * rand();
    kw += (rand() - 0.5) * 0.35;
    // The annual peak: a mid-January cold snap.
    //
    // Pinned to a calendar date rather than an offset from the series start, and
    // to mid-month rather than an edge, so it stays unambiguously winter. The
    // boost is sized to exceed any other interval by a clear margin so the
    // peak cannot wander off the date the demo's own script talks about.
    if (ts.getUTCMonth() === 0 && ts.getUTCDate() === 15 && hour >= 18 && hour < 20) {
      kw += 2.5;
    }

    points[i] = {
      // Cheap sequential ids: 35,040 crypto.randomUUID() calls cost ~100ms for
      // values nothing ever reads.
      id: `demo_ami_${i}`,
      project_id: DEMO_PROJECT_ID,
      timestamp: ts.toISOString(),
      kw: Math.max(0.15, Math.round(kw * 1000) / 1000),
      created_at: ts.toISOString(),
    };
  }
  return points;
}

/** Peak kW and its timestamp, as the app's own calculation would find them. */
export function demoSeriesPeak(series: AMIDataPoint[]): { kw: number; timestamp: string } {
  let best = series[0];
  for (const p of series) if (p.kw > best.kw) best = p;
  return { kw: best.kw, timestamp: best.timestamp };
}

function formatSpan(series: AMIDataPoint[]): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${fmt(series[0].timestamp)} - ${fmt(series[series.length - 1].timestamp)}`;
}

export interface DemoProjectSeed {
  /** Preparer block, normally seeded from the signed-in user's Company Profile. */
  preparer?: Partial<Project>;
  utilityId?: string | null;
  utilityName?: string;
}

/**
 * The sample project as it stands at the START of the demo — deliberately
 * almost empty, because watching the fields get filled in is the point. Only
 * the values a real new project would already carry are set here.
 */
export function buildDemoProject(seed: DemoProjectSeed = {}): Partial<Project> {
  return {
    project_name: 'Sample job — 45 Elm Street',
    status: 'in-progress',
    // Deliberately blank, unlike a real new project which starts at 200. The
    // demo types this in, and typing 200 over a pre-filled 200 reads as a
    // glitch rather than a demonstration. The input renders 0 as empty.
    service_rating_amps: 0,
    service_voltage_numeric: 240,
    service_voltage_text: '120/240V single-phase',
    service_phase: 'single',
    service_type: 'unknown',
    installation_status: 'existing',
    methodology_version: 'NEC 220.87 v1.0',
    // See the file header: this is what keeps the demo clear of billing.
    results_unlocked: true,
    ...seed.preparer,
  };
}

/** The AMI fields the app would set itself after a real upload. */
export function demoAmiFields(
  series: AMIDataPoint[],
  utilityName = 'Burlington Electric Department',
): Partial<Project> {
  const peak = demoSeriesPeak(series);
  return {
    ami_data_provider: utilityName,
    ami_measurement_period: formatSpan(series),
    ami_interval_minutes: 15,
    ami_occupancy_status: 'Occupied, typical use',
    ami_data_quality_notes:
      'Sample dataset supplied with the TrueLoad demo. Not utility-issued data.',
    max_recorded_demand_kw: peak.kw,
    peak_timestamp: peak.timestamp,
  };
}

/**
 * The preparer block the demo fills in.
 *
 * Fixed sample values rather than the signed-in user's profile. The demo runs
 * on a fictional job, so a fictional preparer keeps it consistent for everyone
 * watching — and the people it is aimed at have not set up a Company Profile
 * yet, which is what the beat is telling them to go and do. Leaving these blank
 * would also print the report stamped DRAFT — INCOMPLETE, since Prepared by and
 * Preparer email are both required.
 */
export const DEMO_PREPARER: Partial<Project> = {
  prepared_by: 'Les Watts',
  preparer_title: 'Licensed Electrician',
  preparer_license: 'Sample license — set yours in Company Profile',
  preparer_email: 'jake@ethermsolutions.com',
  company_name: 'Sample Electric Co.',
};

/** Contact details for the sample customer, used by the authorization request. */
export const DEMO_CUSTOMER_EMAIL = 'jake@ethermsolutions.com';

/**
 * Turns the replayed equipment rows into real ones, including the concurrency
 * call the UI would normally make.
 *
 * That last part is not optional. `EquipmentSchedule` is what auto-fills
 * `is_concurrent` from the peak season, and it is only mounted on the Equipment
 * tab — so a job rebuilt while the Results tab is showing would arrive with
 * nothing marked concurrent, quietly dropping the new load out of the
 * calculation the demo is narrating.
 */
export function materializeDemoEquipment(
  rows: Partial<Equipment>[],
  season: string | null,
  createdAt: string,
): Equipment[] {
  return rows.map((row, i) => {
    const base: Equipment = {
      id: `demo_equipment_${i}`,
      project_id: DEMO_PROJECT_ID,
      equipment_name: '',
      equipment_type: '',
      source: undefined,
      quantity: 1,
      amps: null,
      kw: null,
      voltage: 240,
      is_concurrent: false,
      concurrency_reason: null,
      sort_order: i,
      created_at: createdAt,
      // Explicit, as App.handleAddEquipment is: the demo's rows should match a
      // real new row key for key.
      load_role: 'proposed',
      value_source: 'nameplate',
      on_laundry_circuit: false,
      ...row,
    };
    if (season && season !== 'Unknown' && base.equipment_type !== '') {
      const { isConcurrent, reason } = getAutoFillValues(base.equipment_type, season);
      base.is_concurrent = isConcurrent;
      base.concurrency_reason = reason;
    }
    return base;
  });
}

/**
 * The dwelling-inventory rows the traditional path builds — the same table as
 * the proposed loads, with `load_role: 'existing'`, exactly as
 * App.handleAddExistingLoad creates them. No concurrency call: that is a
 * measured-method idea, and the traditional engine ignores the flag anyway.
 */
export function materializeDemoInventory(
  rows: Partial<Equipment>[],
  createdAt: string,
): Equipment[] {
  return rows.map((row, i) => ({
    id: `demo_existing_${i}`,
    project_id: DEMO_PROJECT_ID,
    equipment_name: '',
    equipment_type: 'Other',
    source: 'Nameplate',
    quantity: 1,
    amps: null,
    kw: null,
    voltage: 240,
    is_concurrent: false,
    concurrency_reason: null,
    sort_order: i,
    created_at: createdAt,
    load_role: 'existing',
    value_source: 'default',
    on_laundry_circuit: false,
    ...row,
  }));
}

/**
 * What setting a quantity on a Dwelling Profile checklist item seeds — the
 * same row DwellingProfile.setItemQuantity creates, so the demo's inventory is
 * built the way a contractor's would be: named by the catalog label, rated at
 * the catalog's standard value (or left for the nameplate where it has none),
 * and tagged `default` so the report can count the values nobody verified.
 */
export function demoInventorySeed(key: string, quantity = 1): Partial<Equipment> {
  const item = DWELLING_CHECKLIST.find((i) => i.key === key);
  if (!item) throw new Error(`No Dwelling Profile checklist item "${key}"`);
  return {
    equipment_name: item.label,
    equipment_type: item.equipmentType,
    kw: item.defaultKw,
    quantity,
    value_source: item.defaultKw === null ? 'nameplate' : 'default',
    ...item.seed,
  };
}
